import React from 'react';
import PropTypes from 'prop-types';
import { Helmet } from 'react-helmet';
import { configureForm } from '@thinkmill/pragmatic-forms';

import { encode, describe } from 'inside-out/managers/formManager';

import { DropZone } from 'inside-out/components/shared/dropZone';
import { Link } from 'inside-out/components/shared/link';

import {
	wrapperStyle,
	innerWrapperStyle,
	titleStyle,
	textStyle,
	holdAFundraiserFormWrapperStyle,
	holdAFundraiserTextareaStyle,
} from 'inside-out/page-styles/get-involved/detail';

import {
	detailSideBarLayout,
	formSection,
	contentSection,
	button,
	aqua,
	pink,
} from 'inside-out/global-styles';

export default class VolunteerPage extends React.PureComponent {
	static propTypes = {
		data: PropTypes.shape({
			page: PropTypes.shape({
				edges: PropTypes.arrayOf(
					PropTypes.shape({
						node: PropTypes.shape({
							heading: PropTypes.string.isRequired,
							body: PropTypes.string.isRequired,
							subheading: PropTypes.string.isRequired,
						}).isRequired,
					})
				).isRequired,
			}).isRequired,
		}).isRequired,
	};

	state = {
		hasSubmitted: false,
	};

	VolunteerForm = configureForm({
		initFields: () => ({
			'form-name': 'volunteer',
			botField: '',
			firstName: '',
			lastName: '',
			email: '',
			message: '',
			cv: null,
		}),
		validate: ({ firstName, lastName, email, message, cv }) => {
			const errors = {};
			if (!firstName) errors.firstName = 'Please enter your First Name';
			if (!lastName) errors.lastName = 'Please enter your Last Name';
			if (!email) errors.email = 'Please enter an Email Address';
			if (!message) errors.message = 'Please enter a Message';
			if (!cv) errors.cv = 'Please provide your C.V.';
			return errors;
		},
		submit: async formData => {
			try {
				await fetch('/', {
					method: 'POST',
					body: encode(formData),
				});
				this.setState({ hasSubmitted: true });
			} catch (error) {
				console.error(error);
			}
		},
	})(({ form }) => (
		<form.Form netlify="true" name="volunteer" netlify-honeypot="botField">
			<div className={formSection.wrapper}>
				<p style={{ display: 'none' }}>
					<label>
						Don’t fill this out if you&apos;re human:
						<input {...form.getInputProps({ name: 'botField', type: 'text' })} />
					</label>
				</p>
				<div aria-live="assertive">
					{/* Errors that come back from the server */}
					{form.submitError && (
						<p className={formSection.validationMessage}>Error: {form.submitError}</p>
					)}
					{/* Field Validation Errors */}
					{form.hasErrors && (
						<ul className={formSection.validationSummary}>
							{Object.keys(form.errors).map(key => (
								<li key={key} className={formSection.validationMessage}>{`${form.errors[key]}`}</li>
							))}
						</ul>
					)}
				</div>
				<div className={formSection.halfWidthWrapper}>
					<div className={formSection.field}>
						{form.errors['firstName'] && (
							<p className={formSection.validationFieldMessage}>{form.errors['firstName']}</p>
						)}
						<label htmlFor="firstName">First Name</label>
						<input id="firstName" {...form.getInputProps({ name: 'firstName', type: 'text' })} />
					</div>

					<div className={formSection.field}>
						{form.errors['lastName'] && (
							<p className={formSection.validationFieldMessage}>{form.errors['lastName']}</p>
						)}
						<label htmlFor="lastName">Last Name</label>
						<input id="lastName" {...form.getInputProps({ name: 'lastName', type: 'text' })} />
					</div>
				</div>

				<div className={formSection.field}>
					{form.errors['email'] && (
						<p className={formSection.validationFieldMessage}>{form.errors['email']}</p>
					)}
					<label htmlFor="emailAddress">Email Address</label>
					<input id="emailAddress" {...form.getInputProps({ name: 'email', type: 'text' })} />
				</div>

				<div className={formSection.field}>
					{form.errors['message'] && (
						<p className={formSection.validationFieldMessage}>{form.errors['message']}</p>
					)}
					<label htmlFor="message">Tell us why you want to volunteer with InsideOut</label>
					<textarea
						className={holdAFundraiserTextareaStyle}
						id="message"
						{...form.getInputProps({ name: 'message' })}
					/>
				</div>

				<div className={formSection.field}>
					{form.errors['cv'] && (
						<p className={formSection.validationFieldMessage}>{form.errors['cv']}</p>
					)}
					<label htmlFor="cv">Drop your CV here</label>

					<DropZone
						className={formSection.dropZoneField.default}
						{...form.getInputProps({ name: 'cv', type: 'file' })}
					>
						{(file, clearFile) => {
							if (file) {
								return (
									<p>
										<button onClick={clearFile}>Clear</button>
										<span>{file.name}</span>
									</p>
								);
							} else {
								return (
									<p>
										<span>Drop your CV here or</span>
										<span className={button(aqua)}>Choose a file to upload</span>
									</p>
								);
							}
						}}
					</DropZone>
				</div>

				<button
					className={formSection.submitButton}
					type="submit"
					disabled={form.hasErrors || form.isLoading}
				>
					Apply Now
				</button>
			</div>
		</form.Form>
	));

	render() {
		const pageSettings = this.props.data.page.edges[0].node;

		return (
			<section className={wrapperStyle}>
				<Helmet title="Volunteer">
          <meta property="og:type" content="website"/>
          <meta property="og:title" content="Volunteer"/>
          <meta name="description" content="We love having volunteers. There are so many things we hope to do and achieve, and volunteers can help us make some of our dreams a reality."/>
          <meta property="og:description" content="We love having volunteers. There are so many things we hope to do and achieve, and volunteers can help us make some of our dreams a reality."/>
        </Helmet>

				<div className={innerWrapperStyle}>
					<div className={detailSideBarLayout.wrapper}>
						<div className={detailSideBarLayout.leftSide}>
							<div className={contentSection('#fff')}>
								{!this.state.hasSubmitted && (
									<React.Fragment>
										<header>
											<h1 className={titleStyle}>{pageSettings.heading}</h1>
										</header>
										<div
											className={textStyle}
											dangerouslySetInnerHTML={{ __html: pageSettings.body }}
										/>
										<div className={holdAFundraiserFormWrapperStyle}>
											<this.VolunteerForm />
										</div>
									</React.Fragment>
								)}
								{this.state.hasSubmitted && (
									<React.Fragment>
										<header>
											<h1 className={titleStyle}>
												Thank you for submitting your interest in volunteering with InsideOut
											</h1>
										</header>
										<div
											className={textStyle}
											dangerouslySetInnerHTML={{
												__html:
													'We will be in contact with you as relevant volunteer positions become available at InsideOut.',
											}}
										/>
										<div className={contentSection('#fff')}>
											<Link className={button(pink)} to="/get-involved">
												Go to Get Involved
											</Link>
										</div>
									</React.Fragment>
								)}
							</div>
						</div>
					</div>
				</div>
			</section>
		);
	}
}

export const query = graphql`
	query VolunteerPageQuery {
		page: allContentJson(
			filter: { id: { regex: "/content\\/get-involved-volunteer-page\\.json/" } }
		) {
			edges {
				node {
					heading
					body
					subheading
				}
			}
		}
	}
`;



// WEBPACK FOOTER //
// ./src/pages/get-involved/volunteer/index.js